<script setup lang="ts">
import type { NuxtError } from "#app";

const props = defineProps<{ error: NuxtError }>();

const { t } = useI18n();
const localePath = useLocalePath();

const is404 = computed(() => props.error?.statusCode === 404);
const ns = computed(() => (is404.value ? "error.notFound" : "error.generic"));

useHead(() => ({
  title: `${t(`${ns.value}.title`)} ${t("seo.brandSuffix")}`,
  meta: [{ name: "robots", content: "noindex,nofollow" }],
  bodyAttrs: { class: "dark:bg-slate-900" },
}));

useReveal();

function handleHome() {
  clearError({ redirect: localePath("index") });
}

function handleSearch() {
  clearError({ redirect: localePath("/search") });
}
</script>

<template>
  <main>
    <div class="fixed top-4 right-4 z-50 hidden min-[576px]:block">
      <NavbarUtilityControls variant="light" />
    </div>
    <div class="fixed top-4 right-4 z-50 block min-[576px]:hidden">
      <NavbarUtilityControls variant="light" compact />
    </div>

    <section
      class="relative min-h-screen overflow-hidden bg-slate-900 text-white"
    >
      <NuxtImg
        src="/images/complex/2026-04-22-22-35-00-territory-warm-4x3.webp"
        alt=""
        role="presentation"
        class="absolute inset-0 h-full w-full object-cover"
        sizes="100vw"
        preload
      />

      <div
        class="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-900/70 to-slate-900/90"
      />

      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -bottom-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-red-500/5 blur-3xl"
      />

      <div
        class="relative z-10 flex min-h-screen items-center justify-center px-6 py-24"
      >
        <div class="max-w-2xl text-center">
          <p
            data-reveal
            class="font-body text-xs uppercase tracking-[0.4em] text-amber-400/80"
          >
            {{ error?.statusCode || 404 }}
            <span class="mx-2 text-white/30">·</span>
            {{ t(`${ns}.title`) }}
          </p>

          <div
            data-reveal
            data-reveal-delay="1"
            aria-hidden="true"
            class="mt-6 font-display text-[clamp(7rem,22vw,16rem)] leading-none tracking-tight"
          >
            <span>4</span><span class="text-amber-400">0</span><span>4</span>
          </div>

          <div
            data-reveal
            data-reveal-delay="1"
            aria-hidden="true"
            class="mx-auto mt-2 h-px w-24 bg-amber-400/60"
          />

          <h1
            data-reveal
            data-reveal-delay="2"
            class="mt-8 font-display text-3xl italic md:text-4xl"
          >
            {{ t(`${ns}.headline`) }}
          </h1>

          <p
            data-reveal
            data-reveal-delay="2"
            class="mx-auto mt-6 max-w-lg font-body text-base leading-relaxed text-white/80 md:text-lg"
          >
            {{ t(`${ns}.body`) }}
          </p>

          <div
            data-reveal
            data-reveal-delay="3"
            class="mt-10 flex flex-col justify-center gap-3 sm:flex-row sm:gap-4"
          >
            <button
              type="button"
              class="group inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-slate-900 transition hover:scale-[1.02] hover:bg-amber-400/90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-400"
              @click="handleHome"
            >
              <span>{{ t("error.cta.home") }}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                fill="none"
                class="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              >
                <path
                  d="M4 10h12m0 0-4-4m4 4-4 4"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-white backdrop-blur transition hover:border-white/60 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/60"
              @click="handleSearch"
            >
              {{ t("error.cta.search") }}
            </button>
          </div>

          <p
            data-reveal
            data-reveal-delay="3"
            class="mt-12 font-body text-xs uppercase tracking-[0.3em] text-white/40"
          >
            {{ t("error.tagline") }}
          </p>
        </div>
      </div>
    </section>
  </main>
</template>
